import { useQueryClient, UseMutationOptions } from 'react-query';
import produce from 'immer';

import {
  useGetLeaderboardConfigsQuery,
  CreateUpdateLeaderboardConfigMutation,
  CreateUpdateLeaderboardConfigMutationVariables,
  GetLeaderboardConfigsQuery,
  GetLeaderboardConfigAggregateQuery,
  useGetLeaderboardConfigAggregateQuery,
} from 'api';
import { useBootstrapQueryKey, useNotify } from 'hooks';
import { useParams } from 'react-router-dom';

type Mutation = CreateUpdateLeaderboardConfigMutation;
type MutationVariables = CreateUpdateLeaderboardConfigMutationVariables;
type QueryData = GetLeaderboardConfigsQuery;
type ScoresQueryData = GetLeaderboardConfigAggregateQuery;

const useUpdateLeaderboard = () => {
  const { config_id } = useParams<{ config_id: string }>();
  const notify = useNotify();
  const queryClient = useQueryClient();

  const bootstrapQueryKey = useBootstrapQueryKey();
  const queryKey = useGetLeaderboardConfigsQuery.getKey();
  const scoresQueryKey = useGetLeaderboardConfigAggregateQuery.getKey({ id: config_id });

  const mutationOptions: UseMutationOptions<Mutation, unknown, MutationVariables> = {
    onMutate: async ({ data }) => {
      await queryClient.cancelQueries(queryKey);

      const baseState = queryClient.getQueryData<QueryData>(queryKey);
      const nextState =
        baseState &&
        produce(baseState, draftState => {
          const { getLeaderboardConfigs } = draftState;
          const index = getLeaderboardConfigs.findIndex(config => config.id === data.id);
          const prevResult = draftState.getLeaderboardConfigs[index];

          if (index !== -1) {
            draftState.getLeaderboardConfigs[index] = {
              ...prevResult,
              ...data,
            };
          }
        });

      const baseScoresState = queryClient.getQueryData<ScoresQueryData>(scoresQueryKey);
      const nextScoresState =
        baseScoresState &&
        produce(baseScoresState, draftState => {
          draftState.getLeaderboardConfigAggregate.scoring_aggregate = data.scoring_aggregate;
        });

      if (baseState) {
        queryClient.setQueryData<QueryData>(queryKey, nextState);
      }

      if (baseScoresState) {
        queryClient.setQueryData<ScoresQueryData>(scoresQueryKey, nextScoresState);
      }

      return baseState;
    },
    onError: (error, mutationArgs, baseState: QueryData) => {
      if (baseState) {
        queryClient.setQueryData<QueryData>(queryKey, baseState);
      }
      notify.mutationError();
    },
    onSettled: () => {
      void queryClient.refetchQueries(bootstrapQueryKey);
    },
  };

  return mutationOptions;
};

export default useUpdateLeaderboard;
